<template>
  <BaseDialog
    :open="open"
    role="dialog"
    aria-modal="true"
    @close="onClose"
  >
    <template #title>
      Benutzer anlegen
    </template>

    <form
      class="form"
      @submit.prevent="onSubmit"
    >
      <div class="row-two">
        <div class="field">
          <label for="firstName">Vorname</label>
          <input
            id="firstName"
            v-model="form.firstName"
            type="text"
            required
            autocomplete="given-name"
          >
        </div>
        <div class="field">
          <label for="lastName">Nachname</label>
          <input
            id="lastName"
            v-model="form.lastName"
            type="text"
            required
            autocomplete="family-name"
          >
        </div>
      </div>

      <div class="row-two">
        <div class="field">
          <label for="email">E-Mail</label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            autocomplete="username"
          >
        </div>
        <div class="field">
          <label for="role">Rolle</label>
          <select
            id="role"
            v-model.number="form.roleId"
            required
          >
            <option
              v-for="r in roles"
              :key="r.id"
              :value="r.id"
            >
              {{ roleLabel(r) }}
            </option>
          </select>
        </div>
      </div>

      <div class="row-two">
        <div class="field mb-2">
          <label for="password">Passwort</label>
          <input
            id="password"
            v-model="form.password"
            type="password"
            required
            autocomplete="new-password"
            :title="passwordTooltip"
          >

          <div
            class="pwd-meter"
            role="progressbar"
            :aria-valuenow="passwordStrength.percent"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuetext="`Stärke: ${passwordStrength.label}`"
          >
            <div
              class="bar"
              :style="{ width: passwordStrength.percent + '%', backgroundColor: passwordStrength.color }"
            />
          </div>
        </div>

        <div class="field mb-2">
          <label for="password2">Passwort (Wiederholung)</label>
          <input
            id="password2"
            v-model="form.passwordConfirm"
            type="password"
            required
            autocomplete="new-password"
          >
          <p
            v-if="form.password && form.passwordConfirm && !passwordsMatch"
            class="help danger"
            role="alert"
          >
            Passwörter stimmen nicht überein.
          </p>
        </div>
      </div>
      <p class="help text-small w-100 mt-0">
        {{ passwordTooltip }}
      </p>

      <div class="field checkbox-field">
        <label><input
          v-model="form.isActive"
          class="chk-small"
          type="checkbox"
        > Aktiv</label>
      </div>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <div
        class="actions"
        style="display:flex; gap:8px; justify-content:flex-end;"
      >
        <button
          type="button"
          class="btn"
          :disabled="loading"
          @click="onClose"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading || !passwordsMatch || !passwordValid || !form.firstName || !form.lastName"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          /> Anlegen
        </button>
      </div>
    </form>
  </BaseDialog>
</template>

<script setup>
import {ref, onMounted, computed, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import usersApi from '@/api/users';
import {useToast} from 'primevue/usetoast';

const props = defineProps({
  open: { type: Boolean, default: false },
  roles: { type: Array, default: () => [] }
});
const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const error = ref(null);
const roles = computed(() => props.roles || []);
const toast = useToast();

const form = ref({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirm: '',
  roleId: 2,
  isActive: true,
});

const passwordTooltip = 'Mindestens 8 Zeichen, Groß- und Kleinbuchstaben sowie mindestens 1 Sonderzeichen (Zahlen empfohlen).';

function resetForm() {
  form.value = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirm: '',
    roleId: 2,
    isActive: true,
  };
  error.value = null;
}

watch(() => props.open, (val) => {
  if (val) resetForm();
});

function roleLabel(r) {
  if (!r) return '';
  if (r.description && String(r.description).trim() !== '') return r.description;
  const map = {admin: 'Admin', teacher: 'Lehrer', parent: 'Eltern'};
  return map[r.name] || r.name || '';
}

const passwordsMatch = computed(() => form.value.password === form.value.passwordConfirm);

const passwordValid = computed(() => {
  const p = form.value.password || '';
  const lengthOk = p.length >= 8;
  const hasLower = /[a-z]/.test(p);
  const hasUpper = /[A-Z]/.test(p);
  const hasSpecial = /[^A-Za-z0-9]/.test(p);
  return lengthOk && hasLower && hasUpper && hasSpecial;
});

const passwordStrength = computed(() => {
  const pwd = form.value.password || '';
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (pwd.length >= 12) score++;
  const percent = Math.min(100, score * 20);

  if (score <= 1) return {label: 'Schwach', color: 'var(--danger)', percent};
  if (score <= 3) return {label: 'Mittel', color: 'var(--warn)', percent};
  return {label: 'Stark', color: 'var(--success)', percent};
});

function onClose() {
  emit('close');
}

async function loadRoles() {
  const res = await usersApi.roles();
  roles.value = res.data?.data || res.data || [];
}

function computeDisplayName(first, last) {
  const f = String(first || '').trim();
  const l = String(last || '').trim();
  return [f, l].filter(Boolean).join(' ').trim();
}

async function onSubmit() {
  if (!passwordsMatch.value) {
    error.value = 'Passwörter stimmen nicht überein.';
    return;
  }
  if (!passwordValid.value) {
    error.value = 'Das Passwort erfüllt nicht die Mindestanforderungen.';
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const first = form.value.firstName;
    const last = form.value.lastName;
    const displayName = computeDisplayName(first, last);
    const payload = {
      email: form.value.email,
      password: form.value.password,
      roleId: form.value.roleId,
      isActive: form.value.isActive,
      firstName: first,
      lastName: last,
      displayName
      // nameCode wird serverseitig ermittelt
    };
    const res = await usersApi.create(payload);
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
    emit('saved', res.data?.data || res.data);
  } catch (e) {
    const status = e?.response?.status;
    const msg = e?.response?.data?.error?.message || e?.response?.data?.message || '';
    if (status === 409) {
      error.value = msg || 'E-Mail ist bereits vergeben.';
    } else {
      error.value = msg || 'Speichern fehlgeschlagen.';
    }
  } finally {
    loading.value = false;
  }
}

onMounted(loadRoles);
</script>

<style scoped>
.row-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.checkbox-field label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-align: left;
}

input.chk-small {
  width: 16px;
  height: 16px;
  accent-color: var(--success, #10B981);
}

.pwd-meter {
  height: 6px;
  background: var(--border);
  border-radius: 999px;
  overflow: hidden;
  margin-top: 10px;
}

.pwd-meter .bar {
  height: 100%;
  width: 0;
  transition: width 180ms ease;
  background: var(--danger);
}
</style>
